import React from 'react';
import { Box, Button, Container, Typography, Card, CardContent, Alert } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Error, Refresh, Support, ShoppingCart } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const PaymentFailure = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  const errorMessage = searchParams.get('error') || 'Payment processing failed';
  const paymentId = searchParams.get('payment_id');
  const orderId = searchParams.get('order_id');

  const handleRetry = () => {
    // Navigate back to packages page or credit purchase page
    navigate('/packages');
  };

  const contactSupport = () => {
    const subject = encodeURIComponent(`Payment Failure - ${paymentId || orderId || 'Unknown ID'}`);
    const body = encodeURIComponent(`Hello Support Team,\n\nI encountered a payment failure with the following details:\n- Error: ${errorMessage}\n- Payment ID: ${paymentId || 'N/A'}\n- Order ID: ${orderId || 'N/A'}\n\nPlease assist with resolving this issue.`);
    window.location.href = `mailto:support@quantumroi.com?subject=${subject}&body=${body}`;
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Error 
          sx={{ 
            fontSize: 80, 
            color: 'error.main',
            mb: 2
          }} 
        />
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: 'error.main' }}>
          Payment Failed
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary' }}>
          We couldn't process your payment
        </Typography>
      </Box>

      {/* Error Details Card */}
      <Card sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
        <CardContent sx={{ p: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="medium">
              {errorMessage}
            </Typography>
          </Alert>

          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Don't worry, your account has not been charged. This could be due to:
            </Typography>
            <ul style={{ color: 'text.secondary', paddingLeft: '20px' }}>
              <li><Typography variant="body2">Insufficient funds in your account</Typography></li>
              <li><Typography variant="body2">Card authorization failed</Typography></li>
              <li><Typography variant="body2">Network connectivity issues</Typography></li>
              <li><Typography variant="body2">Temporary payment gateway problem</Typography></li>
            </ul>
          </Box>

          {(paymentId || orderId) && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" display="block" gutterBottom>
                Reference ID: {paymentId || orderId}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Please provide this ID when contacting support
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleRetry}
          startIcon={<Refresh />}
          sx={{ px: 4 }}
        >
          Try Again
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={contactSupport}
          startIcon={<Support />}
        >
          Contact Support
        </Button>
        <Button
          variant="text"
          size="large"
          onClick={() => navigate('/packages')}
          startIcon={<ShoppingCart />}
        >
          View Packages
        </Button>
      </Box>

      {/* Support Information */}
      <Box sx={{ mt: 6, p: 3, bgcolor: 'grey.50', borderRadius: 2, textAlign: 'center' }}>
        <Typography variant="body2" gutterBottom fontWeight="medium">
          Need immediate assistance?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Email: support@quantumroi.com | Phone: +1 (555) 123-4567
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Our support team is available 24/7 to help you complete your purchase.
        </Typography>
      </Box>

      {/* Current Balance Display */}
      {user && (
        <Box sx={{ mt: 4, p: 2, bgcolor: 'info.50', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="body2">
            Your current balance remains: <strong>{user.credits} credits</strong>
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default PaymentFailure;